import { getAuthToken } from '~/composables/useHttpOnlyAuth'

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()

  // Use runtimeConfig.public.apiBase which:
  // - In development: defaults to '/api/v1' (uses Nuxt proxy)
  // - In production: set via NUXT_PUBLIC_API_BASE env variable (e.g., https://api.technomac-snc.com/api/v1)
  const baseURL = config.public.apiBase || '/api/v1'

  const api = $fetch.create({
    baseURL,
    credentials: 'include', // Send cookies with requests (HttpOnly cookie support)
    headers: {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    onRequest({ request, options }) {
      if (process.client) {

        // Get CSRF token from cookie (set by Laravel)
        const csrfToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('XSRF-TOKEN='))
          ?.split('=')[1]

        const additionalHeaders: HeadersInit = {}

        if (csrfToken) {
          // Decode the CSRF token (Laravel encodes it)
          ;(additionalHeaders as Record<string, string>)['X-XSRF-TOKEN'] = decodeURIComponent(csrfToken)
        }

        // Add Bearer token if available (for cross-domain auth)
        const authToken = getAuthToken()
        if (authToken) {
          ;(additionalHeaders as Record<string, string>)['Authorization'] = `Bearer ${authToken}`
        }

        // Merge headers for the request
        // ofetch normalizes headers to a Headers instance. Object spread drops its
        // entries, including Accept, so Laravel may redirect validation failures.
        const headers = new Headers(options.headers)
        new Headers(additionalHeaders).forEach((value, name) => headers.set(name, value))
        options.headers = headers
      }
    },
    onResponse({ response }) {
      // Handle successful responses
      if (response && response._data) {
        // Check for rate limiting headers
        const rateLimitRemaining = response.headers?.get('x-ratelimit-remaining')
        if (rateLimitRemaining) {
          const remaining = parseInt(rateLimitRemaining)
          if (remaining < 10 && process.dev) {
            console.warn(`Rate limit warning: ${remaining} requests remaining`)
          }
        }
      }
    },
    onResponseError({ response, error }) {
      if (process.client) {
        let errorMessage = 'An unexpected error occurred. Please try again.'
        let errorCode = 'UNKNOWN_ERROR'
        let shouldLogout = false

        if (response?._data) {
          const data = response._data

          if (data.message) {
            errorMessage = data.message
          }

          if (data.error_code) {
            errorCode = data.error_code
          }

          // Handle specific error codes
          switch (data.error_code) {
            case 'RATE_LIMIT_EXCEEDED':
              errorMessage = 'Too many requests. Please wait a moment and try again.'
              break
            case 'SUSPICIOUS_ACTIVITY':
              errorMessage = 'Suspicious activity detected. Access temporarily restricted.'
              shouldLogout = true
              break
            case 'VALIDATION_ERROR':
              errorMessage = 'Please check your input and try again.'
              break
            case 'UNAUTHENTICATED':
              errorMessage = 'Your session has expired. Please log in again.'
              shouldLogout = true
              break
            case 'UNAUTHORIZED':
              errorMessage = 'You do not have permission to perform this action.'
              break
            case 'RESOURCE_NOT_FOUND':
              errorMessage = 'The requested resource was not found.'
              break
            case 'MODEL_NOT_FOUND':
              errorMessage = 'The requested item was not found.'
              break
          }
        } else {
          // Handle HTTP status codes
          switch (response?.status) {
            case 401:
              errorMessage = 'Your session has expired. Please log in again.'
              shouldLogout = true
              break
            case 403:
              errorMessage = 'You do not have permission to perform this action.'
              break
            case 404:
              errorMessage = 'The requested resource was not found.'
              break
            case 422:
              errorMessage = 'Please check your input and try again.'
              break
            case 429:
              errorMessage = 'Too many requests. Please wait a moment and try again.'
              break
            case 500:
              errorMessage = 'Server error. Please try again later.'
              break
            case 502:
            case 503:
            case 504:
              errorMessage = 'Service temporarily unavailable. Please try again later.'
              break
          }
        }

        // Logout if required
        if (shouldLogout) {
          try {
            const adminStore = useAdminStore()
            adminStore.clearAuth()
          } catch (error) {
            if (process.dev) {
              console.error('Failed to clear auth:', error)
            }
          }
        }

        const enhancedError = new Error(errorMessage) as any
        enhancedError.data = response?._data || {}
        enhancedError.status = response?.status
        enhancedError.code = errorCode

        throw enhancedError
      }
    }
  })

  return {
    provide: {
      api: {
        get: (url: string, options?: { params?: any; query?: any }) => {
          // Support both 'params' (axios-style) and 'query' ($fetch-style)
          const query = options?.query || options?.params
          return api(url, { method: 'GET', query })
        },
        post: (url: string, data?: any) => api(url, { method: 'POST', body: data }),
        put: (url: string, data?: any) => api(url, { method: 'PUT', body: data }),
        patch: (url: string, data?: any) => api(url, { method: 'PATCH', body: data }),
        delete: (url: string) => api(url, { method: 'DELETE' }),
        upload: (url: string, formData: FormData, method: 'POST' | 'PUT' = 'POST') => api(url, {
          method: method,
          body: formData,
        }),
        async getWithRetry(url: string, options?: { params?: any; query?: any }, retries = 3): Promise<any> {
          const query = options?.query || options?.params
          try {
            return await api(url, { method: 'GET', query })
          } catch (error: any) {
            if (retries > 0 && error?.message?.includes('Server error')) {
              await new Promise(resolve => setTimeout(resolve, 1000))
              return this.getWithRetry(url, options, retries - 1)
            }
            throw error
          }
        },
        async getPaginated(url: string, options?: { params?: any; query?: any }): Promise<any> {
          const query = options?.query || options?.params
          const response: any = await api(url, { method: 'GET', query })

          if (response.data && Array.isArray(response.data)) {
            return {
              data: response.data,
              pagination: {
                current_page: query?.page || 1,
                per_page: query?.per_page || 12,
                total: response.data.length,
                last_page: 1,
                from: 1,
                to: response.data.length
              }
            }
          }

          return response
        }
      }
    }
  }
})
